import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { appAPI, downloadAPI, ratingAPI, shareAPI } from '../services/api';

const DownloadEnhanced = () => {
  const { token } = useParams();
  const [appData, setAppData] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState(0);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    
    // Get base URL without /api suffix
    const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'https://apibackend.sisateck.com';
    
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  // Get absolute URL for social media (always use production domain)
  const getAbsoluteImageUrl = (path) => {
    if (!path) return 'https://sisateck.com/logo.png';
    if (path.startsWith('http')) return path;
    
    // Always use production URL for social media
    const baseUrl = 'https://apibackend.sisateck.com';
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  useEffect(() => {
    fetchAppData();
  }, [token]);

  const fetchAppData = async () => {
    try {
      if (token) {
        // Get app ID from share token
        const shareResponse = await shareAPI.getShareLinkByToken(token);
        if (shareResponse.success && shareResponse.data.app_id) {
          const appId = shareResponse.data.app_id;
          const response = await appAPI.getAppById(appId);
          setAppData(response.data);
          
          const ratingsResponse = await ratingAPI.getRatings(appId);
          setRatings(ratingsResponse.data.ratings);
          setLoading(false);
        } else {
          console.error('Share link not found');
          setLoading(false);
        }
      } else {
        // No token - fetch all apps and show the first approved one
        const allAppsResponse = await appAPI.getAllApps();
        const approvedApps = allAppsResponse.data.filter(app => app.status === 'approved');
        
        if (approvedApps.length === 0) {
          console.error('No approved apps found');
          setLoading(false);
          return;
        }
        
        setAppData(approvedApps[0]);
        
        const ratingsResponse = await ratingAPI.getRatings(approvedApps[0].id);
        setRatings(ratingsResponse.data.ratings);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching app data:', error);
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!appData) return;
    
    setDownloading(true);
    setDownloadProgress(0);
    
    try {
      await downloadAPI.recordDownload(appData.id);
      
      // Update local download count immediately
      setAppData(prev => ({
        ...prev,
        total_downloads: (prev.total_downloads || 0) + 1
      }));
      
      const simulateProgress = () => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += Math.random() * 15;
          if (progress >= 100) {
            progress = 100;
            clearInterval(interval);
            setDownloadProgress(100);
            setDownloadComplete(true);
            
            setTimeout(() => {
              setDownloading(false);
              setDownloadComplete(false);
              setDownloadProgress(0);
            }, 2000);
          } else {
            setDownloadProgress(Math.min(progress, 95));
          }
        }, 200);
      };
      
      simulateProgress();
      
      // Trigger actual APK download
      const link = document.createElement('a');
      link.href = getImageUrl(appData.apk_url);
      link.download = `${appData.app_name.replace(/\s/g, '-')}-${appData.version}.apk`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error downloading:', error);
      setDownloading(false);
      
      // Fallback: try direct download even if recording fails
      const link = document.createElement('a');
      link.href = getImageUrl(appData.apk_url);
      link.download = `${appData.app_name.replace(/\s/g, '-')}-${appData.version}.apk`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleGenerateShareLink = async () => {
    if (!appData) return;
    
    setGeneratingLink(true);
    try {
      if (token) {
        // Already have a token, use current URL
        setShareLink(window.location.href);
        setShowShareModal(true);
        setGeneratingLink(false);
      } else {
        // Create new share link
        const response = await shareAPI.createShareLink(
          appData.id,
          `Download ${appData.app_name}`,
          appData.description,
          null
        );
        
        if (response.success) {
          setShareLink(response.data.shareUrl);
          setShowShareModal(true);
        }
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      alert('Failed to generate share link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyToClipboard = async () => {
    if (shareLink) {
      try {
        await navigator.clipboard.writeText(shareLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Copy failed:', error);
      }
    }
  };

  const RatingStars = ({ rating, size = 'sm' }) => {
    const sizeClasses = size === 'lg' ? 'text-2xl' : 'text-sm';
    return (
      <div className={`flex gap-0.5 ${sizeClasses}`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= Math.round(rating) ? 'text-green-500' : 'text-gray-600'}>
            ★
          </span>
        ))}
      </div>
    );
  };

  const ScreenshotModal = () => {
    const screenshots = Array.isArray(appData.screenshots) ? appData.screenshots.filter(url => url) : [];
    
    const nextScreenshot = () => {
      setSelectedScreenshot((prev) => (prev + 1) % screenshots.length);
    };
    
    const prevScreenshot = () => {
      setSelectedScreenshot((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    };
    
    return (
      <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        {/* Close button */}
        <button
          onClick={() => setShowScreenshotModal(false)}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
        >
          <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Previous button */}
        {screenshots.length > 1 && (
          <button
            onClick={prevScreenshot}
            className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-3"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Screenshot */}
        <div className="max-w-5xl max-h-[90vh] flex items-center justify-center">
          <img
            src={getImageUrl(screenshots[selectedScreenshot])}
            alt={`Screenshot ${selectedScreenshot + 1}`}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
          />
        </div>

        {/* Next button */}
        {screenshots.length > 1 && (
          <button
            onClick={nextScreenshot}
            className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-3"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Counter */}
        {screenshots.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
            {selectedScreenshot + 1} / {screenshots.length}
          </div>
        )}
      </div>
    );
  };

  const ShareModal = () => {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-3xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">🔗</div>
            <h3 className="text-2xl font-bold mb-2">Share this app</h3>
            <p className="text-gray-400 text-sm mb-6">
              Copy the link below to share with others
            </p>

            {/* Share Link */}
            <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={shareLink || 'Generating...'}
                  readOnly
                  className="flex-1 bg-transparent text-green-400 text-sm focus:outline-none"
                />
                <button
                  onClick={copyToClipboard}
                  className="flex-shrink-0 bg-green-500 hover:bg-green-400 text-black font-bold px-4 py-2 rounded-lg transition-all"
                >
                  {copied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Share Stats */}
            <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-4 mb-6">
              <div className="text-xs text-gray-500 mb-2">Share Link Benefits</div>
              <ul className="text-sm text-gray-300 space-y-2 text-left">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 flex-shrink-0">✓</span>
                  <span>Track who downloads through your link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 flex-shrink-0">✓</span>
                  <span>Short, easy-to-remember URL</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 flex-shrink-0">✓</span>
                  <span>Never expires unless deactivated</span>
                </li>
              </ul>
            </div>

            <button
              onClick={() => {
                setShowShareModal(false);
                setCopied(false);
              }}
              className="w-full bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white font-semibold py-3 rounded-xl transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const RatingModal = () => {
    const [userRating, setUserRating] = useState(0);
    const [review, setReview] = useState('');
    const [userName, setUserName] = useState('');

    const submitRating = async () => {
      try {
        await ratingAPI.addRating(appData.id, userRating, review, userName);
        setShowRatingModal(false);
        fetchAppData();
      } catch (error) {
        console.error('Error submitting rating:', error);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-3xl p-8 max-w-md w-full">
          <h3 className="text-2xl font-bold mb-6">Rate this app</h3>
          
          <div className="flex justify-center gap-2 mb-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setUserRating(star)}
                className="text-4xl transition-all hover:scale-110"
              >
                <span className={star <= userRating ? 'text-green-500' : 'text-gray-600'}>★</span>
              </button>
            ))}
          </div>

          <input
            type="text"
            placeholder="Your name (optional)"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 mb-4 focus:outline-none focus:border-green-500"
          />

          <textarea
            placeholder="Share your experience (optional)"
            value={review}
            onChange={(e) => setReview(e.target.value)}
            rows={4}
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl px-4 py-3 mb-6 focus:outline-none focus:border-green-500 resize-none"
          />

          <div className="flex gap-3">
            <button
              onClick={() => setShowRatingModal(false)}
              className="flex-1 bg-transparent border border-[#2a2a2a] text-white py-3 rounded-xl hover:bg-white/5 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={submitRating}
              disabled={userRating === 0}
              className="flex-1 bg-green-500 text-black font-bold py-3 rounded-xl hover:bg-green-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-[#0a0a0a] text-white min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading app details...</p>
        </div>
      </div>
    );
  }

  if (!appData) {
    return (
      <div className="bg-[#0a0a0a] text-white min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold mb-2">App not found</p>
          <p className="text-gray-400">Please try again later</p>
        </div>
      </div>
    );
  }

  const screenshots = Array.isArray(appData.screenshots) ? appData.screenshots.filter(url => url) : [];

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen pt-16">
      <Helmet>
        <title>{`${appData.app_name} - ${appData.category} | Download APK`}</title>
        <meta name="description" content={appData.description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="SisaTeck" />
        <meta property="og:title" content={appData.app_name} />
        <meta property="og:description" content={appData.description} />
        <meta property="og:image" content={getAbsoluteImageUrl(appData.icon_url)} />
        <meta property="og:image:secure_url" content={getAbsoluteImageUrl(appData.icon_url)} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content={`${appData.app_name} icon`} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={window.location.href} />
        <meta property="twitter:title" content={appData.app_name} />
        <meta property="twitter:description" content={appData.description} />
        <meta property="twitter:image" content={getAbsoluteImageUrl(appData.icon_url)} />
        
        {/* Additional metadata */}
        <meta property="og:locale" content="en_US" />
        <meta name="author" content={appData.developer} />
      </Helmet>
      
      {showRatingModal && <RatingModal />}
      {showShareModal && <ShareModal />}
      {showScreenshotModal && <ScreenshotModal />}
      
      {/* DOWNLOADING MODAL */}
      {downloading && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-3xl p-8 max-w-md w-full">
            <div className="text-center">
              {/* App Icon */}
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-green-500/20 rounded-2xl blur-xl animate-pulse"></div>
                <img
                  src={getImageUrl(appData.icon_url)}
                  alt={appData.app_name}
                  className="relative w-20 h-20 rounded-2xl object-cover border-2 border-green-500/30"
                  style={{ background: 'white' }}
                />
              </div>

              <h3 className="text-2xl font-bold mb-2">
                {downloadComplete ? 'Download Complete!' : 'Downloading...'}
              </h3>
              <p className="text-gray-400 text-sm mb-6">
                {downloadComplete 
                  ? `${appData.app_name} is ready to install`
                  : `Getting ${appData.app_name} ready for you`
                }
              </p>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400">Progress</span>
                  <span className="text-green-400 font-bold">{Math.round(downloadProgress)}%</span>
                </div>
                <div className="w-full bg-[#2a2a2a] rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-green-500 to-green-400 h-full rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                    style={{ width: `${downloadProgress}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                  </div>
                </div>
              </div>

              {/* Download Info */}
              <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400">File Size</span>
                  <span className="text-white font-semibold">{appData.size_mb} MB</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Version</span>
                  <span className="text-white font-semibold">{appData.version}</span>
                </div>
              </div>

              {/* Status Icons */}
              {downloadComplete ? (
                <div className="flex items-center justify-center gap-2 text-green-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-semibold">Download successful!</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3 text-gray-400 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Downloading APK</span>
                  </div>
                  <span>•</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
                    <span>Installing</span>
                  </div>
                </div>
              )}

              {downloadComplete && (
                <div className="mt-6 bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <div className="text-green-400 font-semibold text-sm mb-2">📁 How to Find Your Download:</div>
                  <ul className="text-xs text-gray-400 space-y-1.5 text-left">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span><strong>Browser:</strong> Check Downloads or notification bar</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span><strong>File Manager:</strong> Look in Downloads folder</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span><strong>File name:</strong> {appData.app_name.replace(/\s/g, '-')}-{appData.version}.apk</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PLAY STORE HEADER */}
      <section className="bg-[#111111] border-b border-[#2a2a2a] py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6">
            {/* App Icon */}
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 bg-green-500/20 rounded-3xl blur-2xl"></div>
              <img
                src={getImageUrl(appData.icon_url)}
                alt={appData.app_name}
                className="relative w-28 h-28 md:w-32 md:h-32 rounded-3xl object-cover border-2 border-green-500/20 shadow-2xl"
                style={{ background: 'white' }}
              />
            </div>

            {/* App Info */}
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-black mb-2">{appData.app_name}</h1>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-green-400 font-semibold">{appData.developer}</span>
                <span className="text-gray-600">·</span>
                <span className="text-gray-400 text-sm">{appData.category}</span>
              </div>
              
              <p className="text-gray-300 text-base mb-6 leading-relaxed max-w-3xl">
                {appData.description}
              </p>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-6 mb-6">
                <div className="text-center">
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-2xl font-bold text-green-500">{appData.rating}</span>
                    <span className="text-gray-500 text-sm">★</span>
                  </div>
                  <div className="text-xs text-gray-500">{appData.total_ratings?.toLocaleString()} ratings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {appData.total_downloads >= 1000 
                      ? `${(appData.total_downloads / 1000).toFixed(1)}K+`
                      : `${appData.total_downloads}+`
                    }
                  </div>
                  <div className="text-xs text-gray-500">Downloads</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">{appData.min_android_version}</div>
                  <div className="text-xs text-gray-500">Minimum</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{appData.size_mb} MB</div>
                  <div className="text-xs text-gray-500">Size</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleDownload}
                  className="group relative inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black font-black text-base px-8 py-4 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/30"
                >
                  <span className="text-xl">⬇️</span>
                  Download APK
                </button>
                <button
                  onClick={handleGenerateShareLink}
                  disabled={generatingLink}
                  className="inline-flex items-center gap-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200 disabled:opacity-50"
                >
                  <span>🔗</span>
                  {generatingLink ? 'Generating...' : 'Share'}
                </button>
                <button
                  onClick={() => setShowRatingModal(true)}
                  className="inline-flex items-center gap-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200"
                >
                  <span>⭐</span>
                  Rate App
                </button>
                <a
                  href="https://wa.me/254793973389"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200"
                >
                  <span>💬</span>
                  Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCREENSHOTS */}
      {screenshots.length > 0 && (
        <section className="py-8 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a] border-b border-[#2a2a2a]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-xl font-bold mb-4">Screenshots</h2>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {screenshots.map((screenshotUrl, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setSelectedScreenshot(idx);
                    setShowScreenshotModal(true);
                  }}
                  className="flex-shrink-0 cursor-pointer transform hover:scale-105 transition-transform hover:opacity-80"
                >
                  <img
                    src={getImageUrl(screenshotUrl) || '/images/screenshot-placeholder.png'}
                    alt={`Screenshot ${idx + 1}`}
                    className="h-64 w-auto rounded-2xl border border-[#2a2a2a] object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RATINGS & REVIEWS */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#111111] border-b border-[#2a2a2a]">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold">Ratings and reviews</h2>
            <button
              onClick={() => setShowRatingModal(true)}
              className="text-green-400 hover:text-green-300 font-semibold text-sm"
            >
              Write a review
            </button>
          </div>

          {/* Rating Summary */}
          <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-2xl p-8 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex flex-col items-center justify-center">
                <div className="text-6xl font-black mb-2">{appData.rating}</div>
                <RatingStars rating={appData.rating} size="lg" />
                <div className="text-gray-400 text-sm mt-2">{appData.total_ratings?.toLocaleString()} reviews</div>
              </div>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = ratings.filter(r => r.rating === star).length;
                  const percentage = ratings.length > 0 ? (count / ratings.length) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-3">
                      <span className="text-sm w-4">{star}</span>
                      <div className="flex-1 bg-[#2a2a2a] rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-green-500 h-full rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-500 w-12 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="space-y-4">
            {ratings.slice(0, showAllReviews ? ratings.length : 5).map((rating, idx) => (
              <div key={idx} className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 font-bold flex-shrink-0">
                    {rating.user_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{rating.user_name || 'Anonymous'}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(rating.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <RatingStars rating={rating.rating} />
                    {rating.review && (
                      <p className="text-gray-300 text-sm mt-3 leading-relaxed">{rating.review}</p>
                    )}
                    
                    {/* Admin Reply */}
                    {rating.admin_reply && (
                      <div className="mt-4 bg-green-500/5 border border-green-500/20 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-black font-bold flex-shrink-0 text-sm">
                            A
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-green-400 text-sm">Developer Response</span>
                              {rating.admin_reply_at && (
                                <span className="text-xs text-gray-500">
                                  {new Date(rating.admin_reply_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-300 text-sm leading-relaxed">{rating.admin_reply}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* See More Button */}
          {ratings.length > 5 && !showAllReviews && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAllReviews(true)}
                className="inline-flex items-center gap-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white font-semibold px-8 py-3 rounded-xl transition-all"
              >
                See more reviews ({ratings.length - 5} more)
              </button>
            </div>
          )}

          {/* Show Less Button */}
          {showAllReviews && ratings.length > 5 && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAllReviews(false)}
                className="inline-flex items-center gap-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white font-semibold px-8 py-3 rounded-xl transition-all"
              >
                Show less
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ABOUT THIS APP */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a] border-b border-[#2a2a2a]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">About this app</h2>
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 mb-6">
            <p className="text-gray-300 text-base leading-relaxed whitespace-pre-line">
              {appData.long_description}
            </p>
          </div>

          {/* Features */}
          {appData.features && appData.features.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {appData.features.map((feature, idx) => (
                <div key={idx} className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-5 hover:border-green-500/30 transition-colors">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl flex-shrink-0">{feature.feature_icon}</span>
                    <div>
                      <h3 className="text-white font-bold text-base mb-1">{feature.feature_title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{feature.feature_description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* What's New */}
          {appData.whats_new && (
            <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                <span className="text-green-500">🆕</span>
                What's New in {appData.version}
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                {appData.whats_new}
              </p>
            </div>
          )}
        </div>
      </section>


      {/* APP INFO */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#111111] border-t border-[#2a2a2a]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">App info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-[#2a2a2a]">
                <span className="text-gray-400">Version</span>
                <span className="font-semibold">{appData.version}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-[#2a2a2a]">
                <span className="text-gray-400">Updated on</span>
                <span className="font-semibold">{new Date(appData.updated_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-[#2a2a2a]">
                <span className="text-gray-400">Downloads</span>
                <span className="font-semibold">{appData.total_downloads?.toLocaleString()}+</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-[#2a2a2a]">
                <span className="text-gray-400">Size</span>
                <span className="font-semibold">{appData.size_mb} MB</span>
              </div>
              <div className="flex justify-between py-3 border-b border-[#2a2a2a]">
                <span className="text-gray-400">Requires Android</span>
                <span className="font-semibold">{appData.min_android_version} and up</span>
              </div>
              <div className="flex justify-between py-3 border-b border-[#2a2a2a]">
                <span className="text-gray-400">Developer</span>
                <span className="font-semibold text-green-400">{appData.developer}</span>
              </div>
            </div>
          </div>

          {/* Contact Support */}
          <div className="mt-8 bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
            <h3 className="text-lg font-bold mb-4">Need help?</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://wa.me/254793973389"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 justify-center bg-green-500 hover:bg-green-400 text-black font-semibold px-6 py-3 rounded-xl transition-all"
              >
                <span>💬</span>
                WhatsApp Support
              </a>
              <a
                href="tel:+254717325657"
                className="flex items-center gap-2 justify-center bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white font-semibold px-6 py-3 rounded-xl transition-all"
              >
                <span>📞</span>
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadEnhanced;
